/**
 * Batch upload progress: how far along the whole batch is, how much longer it is
 * expected to take, and a per-file breakdown.
 *
 * The remaining-time line is the point of this component. Before it, a multi-file
 * upload was a spinner with no indication whether it would take five seconds or
 * five minutes, which is what made large batches feel broken rather than slow.
 */

import { useMemo, useState } from "react";
import { CaretDown, CaretUp, X } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  formatBytes,
  formatRemaining,
  formatThroughput,
} from "shared/utils/uploadFormat";
import type {
  UploadQueueSummary,
  UploadTaskStatus,
} from "shared/hooks/uploadQueue.types";
import { UploadFileProgressRow } from "./UploadFileProgressRow";

interface UploadProgressPanelProps {
  tasks: UploadTaskStatus[];
  summary: UploadQueueSummary;
  onCancelAll?: () => void;
  onRetry?: (task: UploadTaskStatus) => void;
  /** Hides the panel once the user has read the outcome. */
  onDismiss?: () => void;
  /** Collapse the file list by default; useful for very large batches. */
  defaultCollapsed?: boolean;
  /**
   * `docked` pins the panel to the bottom-right of the viewport, the way Google
   * Drive shows uploads: it stays visible while the user scrolls the file list or
   * keeps working, instead of scrolling away at the top of the page.
   *
   * `inline` keeps it in the document flow, for places that already constrain the
   * layout - the upload modal renders it inside its own overlay.
   */
  variant?: "inline" | "docked";
}

export const UploadProgressPanel = ({
  tasks,
  summary,
  onCancelAll,
  onRetry,
  onDismiss,
  defaultCollapsed = false,
  variant = "inline",
}: UploadProgressPanelProps) => {
  const isDocked = variant === "docked";

  // Docked starts collapsed unless told otherwise: the point of the dock is to
  // stay out of the way, and the summary line alone answers "how far along is it".
  const [isCollapsed, setIsCollapsed] = useState(
    defaultCollapsed || isDocked,
  );

  const percent = Math.round(summary.fraction * 100);

  const headline = useMemo(() => {
    if (summary.isRunning) return "Bestanden worden geüpload";
    if (summary.failed > 0 && summary.succeeded > 0) return "Upload deels gelukt";
    if (summary.failed > 0) return "Upload mislukt";
    if (summary.canceled > 0 && summary.succeeded === 0) return "Upload geannuleerd";
    return "Upload voltooid";
  }, [summary.isRunning, summary.failed, summary.succeeded, summary.canceled]);

  // Sort so the files that need attention are not buried under the finished ones.
  const orderedTasks = useMemo(() => {
    const weight = (task: UploadTaskStatus) => {
      if (task.state === "error") return 0;
      if (task.state === "uploading" || task.state === "processing") return 1;
      if (task.state === "compressing") return 2;
      if (task.state === "pending") return 3;
      if (task.state === "canceled") return 4;
      return 5; // success
    };
    return [...tasks].sort((a, b) => weight(a) - weight(b));
  }, [tasks]);

  if (tasks.length === 0) return null;

  return (
    <section
      className={
        isDocked
          ? // Bottom-right on desktop, full-width strip on mobile. z-50 keeps it
            // above the page but below modals, which sit higher.
            "border-border bg-card fixed right-0 bottom-0 z-50 w-full rounded-t-lg border p-4 shadow-lg sm:right-4 sm:bottom-4 sm:w-[420px] sm:rounded-lg"
          : "border-border bg-card mb-6 rounded-lg border p-5 shadow-sm"
      }
      aria-live="polite"
      aria-busy={summary.isRunning}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {summary.isRunning && (
            <div
              className="border-foreground/20 border-t-moss-primary mt-1 h-5 w-5 shrink-0 animate-spin rounded-full border-2"
              aria-hidden
            />
          )}
          <div className="min-w-0">
            <h2
              className={`text-foreground font-semibold ${
                isDocked ? "text-sm" : "text-lg"
              }`}
            >
              {headline}
            </h2>
            <p
              className={`text-muted-foreground mt-0.5 ${
                isDocked ? "text-xs" : "text-sm"
              }`}
            >
              {summary.succeeded} van {summary.total} bestand
              {summary.total === 1 ? "" : "en"} geüpload
              {summary.failed > 0 && ` · ${summary.failed} mislukt`}
              {summary.canceled > 0 && ` · ${summary.canceled} geannuleerd`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {summary.isRunning && onCancelAll && (
            <Button variant="outline" size="sm" onClick={onCancelAll}>
              <X size={14} aria-hidden />
              Annuleren
            </Button>
          )}
          {!summary.isRunning && onDismiss && (
            <Button variant="outline" size="sm" onClick={onDismiss}>
              Sluiten
            </Button>
          )}
        </div>
      </div>

      {/* The percentage sits next to the bar rather than below it, so the single
          most-asked question ("how far along is this?") is answered at a glance. */}
      <div className="mt-4 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <Progress value={percent} aria-label="Totale uploadvoortgang" />
        </div>
        <span className="text-foreground shrink-0 text-sm font-semibold tabular-nums">
          {percent}%
        </span>
      </div>

      {/* Everything else the user needs to decide whether to wait or walk away. */}
      <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {summary.isRunning && (
          <span className="text-foreground font-medium">
            {formatRemaining(summary.etaMs)}
          </span>
        )}
        <span>
          {formatBytes(summary.bytesSent)} van {formatBytes(summary.bytesTotal)}
        </span>
        {summary.isRunning && summary.throughputBytesPerSecond !== null && (
          <span>{formatThroughput(summary.throughputBytesPerSecond)}</span>
        )}
        {summary.bytesSavedByCompression > 0 && (
          <span className="text-moss-primary">
            {formatBytes(summary.bytesSavedByCompression)} bespaard door
            compressie
          </span>
        )}
      </div>

      <Separator />

      <div className="mt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed((prev) => !prev)}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? (
            <CaretDown size={14} aria-hidden />
          ) : (
            <CaretUp size={14} aria-hidden />
          )}
          {isCollapsed
            ? `Details tonen (${tasks.length})`
            : "Details verbergen"}
        </Button>
      </div>

      {!isCollapsed && (
        <ul
          className={`mt-1 overflow-y-auto ${
            isDocked ? "max-h-[260px]" : "max-h-[420px]"
          }`}
        >
          {orderedTasks.map((task) => (
            <UploadFileProgressRow
              key={task.id}
              task={task}
              // Retrying mid-batch would race the running pool.
              onRetry={summary.isRunning ? undefined : onRetry}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

export default UploadProgressPanel;
